import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { loginUser, registerUser, getCurrentUser, logoutSession, refreshSession, isNetworkError } from '../utils/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

const store = {
  get: (key) => { try { return localStorage.getItem(key); } catch { return null; } },
  set: (key, value) => { try { localStorage.setItem(key, value); } catch { /* unavailable */ } },
  remove: (key) => { try { localStorage.removeItem(key); } catch { /* unavailable */ } },
};

/**
 * A stored session the server rejected (refresh 401/403): one notice with a
 * "Sign in" action (the header opens the sign-in dialog on `anum:open-auth`).
 * Never on an explicit logout. Language from <html lang> (AuthProvider sits
 * inside LanguageProvider in the app, but not in every test).
 */
const noticeSessionExpired = () => {
  // <html lang> is 'ar' before React boots and 'ar-AE' afterwards.
  const ar = typeof document !== 'undefined' && /^ar\b/i.test(document.documentElement.lang || '');
  toast.info(ar ? 'انتهت جلستك. يرجى تسجيل الدخول مرة أخرى.' : 'Your session has expired. Please sign in again.', {
    id: 'session-expired',
    action: {
      label: ar ? 'تسجيل الدخول' : 'Sign in',
      onClick: () => window.dispatchEvent(new CustomEvent('anum:open-auth', { detail: { mode: 'login' } })),
    },
  });
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => store.get('token'));
  const [loading, setLoading] = useState(true);

  const saveSession = useCallback((response) => {
    const accessToken = response.access_token || response.token;
    if (!accessToken) throw new Error('Unexpected response from the server.');
    store.set('token', accessToken);
    if (response.refresh_token) store.set('refresh_token', response.refresh_token);
    setToken(accessToken);
    return accessToken;
  }, []);

  const clearSession = useCallback(() => {
    setUser(null);
    setToken(null);
    store.remove('token');
    store.remove('refresh_token');
  }, []);

  /** Sign out: revoke the tokens server-side (best effort), then locally. */
  const logout = useCallback(() => {
    logoutSession(store.get('token'), store.get('refresh_token'));
    clearSession();
  }, [clearSession]);

  const fetchUser = useCallback(async () => {
    const current = store.get('token');
    if (!current) { setLoading(false); return; }
    try {
      setUser(await getCurrentUser(current));
    } catch (error) {
      if (error.status === 401 || error.status === 403) {
        // Expired access token: try the refresh token once (it rotates).
        const refreshToken = store.get('refresh_token');
        try {
          if (!refreshToken) throw error;
          const fresh = saveSession(await refreshSession(refreshToken));
          setUser(await getCurrentUser(fresh));
        } catch (refreshError) {
          // Only a rejected refresh ends the session; network errors keep it.
          if (!refreshToken || refreshError.status === 401 || refreshError.status === 403) {
            clearSession();
            noticeSessionExpired();
          }
        }
      }
      // Network error / cold start / 5xx: keep the session and try again later.
    } finally {
      setLoading(false);
    }
  }, [clearSession, saveSession]);

  useEffect(() => {
    if (token) {
      fetchUser();
    } else {
      setLoading(false);
    }
    // fetchUser reads the stored token; re-run only when the token changes.
  }, [token]); // eslint-disable-line

  const login = async (email, password) => {
    try {
      const response = await loginUser(email, password);
      saveSession(response);
      setUser(response.user);
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message, status: error.status, network: isNetworkError(error) };
    }
  };

  const register = async (userData) => {
    try {
      const response = await registerUser(userData);
      saveSession(response);
      setUser(response.user);
      // `user.email_verified: false` → the modal shows "Verify your email" (G69).
      return { success: true, user: response.user, verificationEmailSent: response.verification_email_sent };
    } catch (error) {
      return { success: false, error: error.message, status: error.status, network: isNetworkError(error) };
    }
  };

  /** A session from another sign-in route (POST /auth/google, G75). */
  const adoptSession = useCallback((response) => {
    saveSession(response);
    setUser(response.user);
  }, [saveSession]);

  /** After POST /auth/verify-email: the address is confirmed (no extra /auth/me). */
  const markEmailVerified = useCallback(() => {
    setUser((current) => (current ? { ...current, email_verified: true } : current));
  }, []);

  return (
    <AuthContext.Provider value={{ user, token, loading, login, register, logout, refreshUser: fetchUser, markEmailVerified, adoptSession }}>
      {children}
    </AuthContext.Provider>
  );
};
